/** NSDL: IN + 14 digits (16 characters). CDSL: 16 digits. */
export const DEMAT_MAX_LENGTH = 16;

export const DEMAT_NSdl_REGEX = /^IN\d{14}$/;
export const DEMAT_CDSL_REGEX = /^\d{16}$/;
export const DEMAT_COMBINED_REGEX = /^(IN\d{14}|\d{16})$/;

export const DEMAT_FORMAT_HINT = 'NSDL: IN + 14 digits · CDSL: 16 digits';

/**
 * Restrict typing to valid NSDL/CDSL characters and max length.
 * @param {string} value
 * @returns {string}
 */
export function formatDematInput(value) {
  const raw = String(value || '')
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, '');

  if (raw.startsWith('IN')) {
    const digits = raw.slice(2).replace(/\D/g, '').slice(0, 14);
    return `IN${digits}`;
  }

  if (raw.startsWith('I')) {
    if (raw.length === 1) return 'I';
    if (raw[1] === 'N') {
      const digits = raw.slice(2).replace(/\D/g, '').slice(0, 14);
      return `IN${digits}`;
    }
    return raw.slice(1).replace(/\D/g, '').slice(0, 16);
  }

  return raw.replace(/\D/g, '').slice(0, 16);
}

/**
 * @param {string} value
 * @returns {{ valid: boolean; type?: 'NSDL' | 'CDSL'; message: string | null }}
 */
export function validateDematNumber(value) {
  const normalized = String(value || '').trim().toUpperCase();

  if (!normalized) {
    return { valid: false, message: 'Please enter demat account number.' };
  }

  if (DEMAT_NSdl_REGEX.test(normalized)) {
    return { valid: true, type: 'NSDL', message: null };
  }

  if (DEMAT_CDSL_REGEX.test(normalized)) {
    return { valid: true, type: 'CDSL', message: null };
  }

  return {
    valid: false,
    message: 'Enter a valid demat number (NSDL: IN + 14 digits, CDSL: 16 digits).',
  };
}

/**
 * Inline validation for onChange — surfaces errors once input is complete or clearly invalid.
 * @param {string} value
 * @returns {string | null}
 */
export function getDematInputError(value) {
  const normalized = String(value || '').trim().toUpperCase();
  if (!normalized) return null;

  const check = validateDematNumber(normalized);
  if (check.valid) return null;

  if (normalized.length >= DEMAT_MAX_LENGTH) {
    return check.message;
  }

  if (normalized.startsWith('IN') || normalized === 'I') {
    return null;
  }

  if (/^\d+$/.test(normalized) && normalized.length < DEMAT_MAX_LENGTH) {
    return null;
  }

  if (normalized.length >= 2) {
    return check.message;
  }

  return null;
}

/**
 * Format and validate demat input in one step for onChange handlers.
 * @param {string} rawValue
 * @returns {{ value: string; error: string | null }}
 */
export function processDematInputChange(rawValue) {
  const value = formatDematInput(rawValue);
  const error = getDematInputError(value);
  return { value, error };
}
